import type { CanvasRenderContext } from '../core';
import { InteractiveObject } from '../core/editor-object';
import type { ObjectProps } from '../core/editor-object';
import { copyCanvasElement } from '../util/misc/dom';
import type { TSize } from '../typedefs';
import { EditorRasterImage } from './raster-image';
import type { EditorRasterLoadOptions, EditorRasterSource, EditorRasterSourceDescriptor } from './types';

type ReplaceSurfaceOptions = {
  updateOriginal?: boolean;
  clearDraft?: boolean;
};

export class RasterImageLayer extends InteractiveObject {
  override type = 'raster-image-layer';

  readonly source: EditorRasterSourceDescriptor;

  private originalSurface: HTMLCanvasElement;
  private appliedSurface: HTMLCanvasElement;
  private draftSurface?: HTMLCanvasElement;

  constructor(surface: HTMLCanvasElement, source: EditorRasterSourceDescriptor) {
    super({
      type: 'raster-image-layer',
      originX: 'left',
      originY: 'top',
      left: 0,
      top: 0,
      width: surface.width || 1,
      height: surface.height || 1,
      strokeWidth: 0,
      fill: null,
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      hoverCursor: null,
    });
    this.source = source;
    this.originalSurface = copyCanvasElement(surface);
    this.appliedSurface = copyCanvasElement(surface);
    this.syncSizeFromSurface(this.appliedSurface);
  }

  static async fromSource(
    source: EditorRasterSource,
    options: EditorRasterLoadOptions = {},
  ): Promise<RasterImageLayer> {
    const image = await EditorRasterImage.fromSource(source, options);
    return RasterImageLayer.fromRasterImage(image);
  }

  static fromRasterImage(image: EditorRasterImage): RasterImageLayer {
    return new RasterImageLayer(image.snapshot(), image.descriptor);
  }

  getCurrentSize(includeDraft = true): TSize {
    const surface = this.getRenderableElement(includeDraft);
    return {
      width: Math.max(surface.width || 1, 1),
      height: Math.max(surface.height || 1, 1),
    };
  }

  getOriginalSize(): TSize {
    return {
      width: Math.max(this.originalSurface.width || 1, 1),
      height: Math.max(this.originalSurface.height || 1, 1),
    };
  }

  getAppliedSurface(): HTMLCanvasElement {
    return this.appliedSurface;
  }

  getDraftSurface(): HTMLCanvasElement | undefined {
    return this.draftSurface;
  }

  hasDraft(): boolean {
    return Boolean(this.draftSurface);
  }

  getRenderableElement(includeDraft = true): HTMLCanvasElement {
    return includeDraft && this.draftSurface ? this.draftSurface : this.appliedSurface;
  }

  ensureDraftSurface(): HTMLCanvasElement {
    if (!this.draftSurface) {
      this.draftSurface = copyCanvasElement(this.appliedSurface);
      this.syncSizeFromSurface(this.draftSurface);
    }
    return this.draftSurface;
  }

  setDraftSurface(surface: HTMLCanvasElement): this {
    this.draftSurface = copyCanvasElement(surface);
    this.syncSizeFromSurface(this.draftSurface);
    return this;
  }

  applyDraft(): this {
    if (!this.draftSurface) {
      return this;
    }
    this.appliedSurface = copyCanvasElement(this.draftSurface);
    this.draftSurface = undefined;
    this.syncSizeFromSurface(this.appliedSurface);
    return this;
  }

  rollbackDraft(): this {
    if (!this.draftSurface) {
      return this;
    }
    this.draftSurface = undefined;
    this.syncSizeFromSurface(this.appliedSurface);
    return this;
  }

  restoreAppliedSurface(surface: HTMLCanvasElement): this {
    return this.replaceAppliedSurface(surface, {
      updateOriginal: false,
      clearDraft: true,
    });
  }

  replaceAppliedSurface(surface: HTMLCanvasElement, options: ReplaceSurfaceOptions = {}): this {
    this.appliedSurface = copyCanvasElement(surface);
    if (options.updateOriginal) {
      this.originalSurface = copyCanvasElement(surface);
    }
    if (options.clearDraft !== false) {
      this.draftSurface = undefined;
    }
    this.syncSizeFromSurface(this.appliedSurface);
    return this;
  }

  reset(): this {
    this.appliedSurface = copyCanvasElement(this.originalSurface);
    this.draftSurface = undefined;
    this.syncSizeFromSurface(this.appliedSurface);
    return this;
  }

  snapshot(includeDraft = true): HTMLCanvasElement {
    return copyCanvasElement(this.getRenderableElement(includeDraft));
  }

  override render(ctx: CanvasRenderingContext2D, _context: CanvasRenderContext): void {
    const element = this.getRenderableElement();
    const [a, b, c, d, e, f] = this.calcTransformMatrix();

    ctx.save();
    ctx.globalAlpha = this.opacity;
    ctx.globalCompositeOperation = this.globalCompositeOperation;
    ctx.transform(a, b, c, d, e, f);
    ctx.drawImage(element, -this.width / 2, -this.height / 2, this.width, this.height);
    ctx.restore();
  }

  override toObject(): Partial<ObjectProps> & { source: EditorRasterSourceDescriptor; imageWidth: number; imageHeight: number } {
    return {
      ...super.toObject(),
      source: this.source,
      imageWidth: this.getCurrentSize().width,
      imageHeight: this.getCurrentSize().height,
    };
  }

  private syncSizeFromSurface(surface: HTMLCanvasElement): void {
    this.width = Math.max(surface.width || 1, 1);
    this.height = Math.max(surface.height || 1, 1);
    this.dirty = true;
    this.setCoords();
  }
}
